import React, {
  useEffect,
  useRef,
  useState,
} from "react";

import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import {
  FiActivity,
  FiAlertCircle,
  FiArrowUp,
  FiBarChart2,
  FiCpu,
  FiFileText,
  FiLoader,
  FiPlus,
  FiShield,
  FiTrendingDown,
} from "react-icons/fi";

import { useClient } from "../../context/ClientContext";
import { aiService } from "../../services/aiService";

const MAX_MESSAGE_LENGTH = 4000;
const MAX_HISTORY = 10;

const SUGGESTIONS = [
  {
    title: "My financial overview",
    description: "Understand your available financial records",
    icon: FiBarChart2,
    prompt:
      "Summarize the available financial information for my business. Only use verified records and explain what data is unavailable.",
  },
  {
    title: "Recent transactions",
    description: "Review recent business activity",
    icon: FiActivity,
    prompt:
      "Review my business's recent available transactions. Summarize what you can verify and identify anything that may need attention.",
  },
  {
    title: "Understand expenses",
    description: "Explore your business expense accounts",
    icon: FiTrendingDown,
    prompt:
      "Explain the expense accounts available in my organization's chart of accounts. Do not invent expense amounts.",
  },
  {
    title: "Questions for my accountant",
    description: "Prepare for your next financial discussion",
    icon: FiFileText,
    prompt:
      "Based on the available records for my business, what useful questions should I ask my accountant?",
  },
];

function createMessage(role, content) {
  return {
    id:
      typeof crypto !== "undefined" &&
      typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random()}`,
    role,
    content,
  };
}

function ChatBubble({ message }) {
  const isUser = message.role === "user";

  return (
    <div
      className={`flex w-full gap-3 ${
        isUser ? "justify-end" : "justify-start"
      }`}
    >
      {!isUser && (
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-white">
          <FiCpu />
        </div>
      )}

      <div
        className={
          isUser
            ? "max-w-[85%] sm:max-w-[75%]"
            : "min-w-0 max-w-[calc(100%-3rem)] flex-1"
        }
      >
        {!isUser && (
          <p className="mb-2 text-xs font-semibold text-slate-600">
            ProfitYo AI Copilot
          </p>
        )}

        {isUser ? (
          <div className="whitespace-pre-wrap break-words rounded-2xl rounded-br-md bg-slate-900 px-4 py-3 text-sm leading-7 text-white">
            {message.content}
          </div>
        ) : (
          <div className="min-w-0 overflow-hidden rounded-2xl rounded-bl-md border border-slate-200 bg-white px-4 py-4 text-sm leading-7 text-slate-700 shadow-sm sm:px-5">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h1: ({ node, ...props }) => (
                  <h1
                    className="mb-4 mt-2 text-xl font-bold text-slate-900"
                    {...props}
                  />
                ),

                h2: ({ node, ...props }) => (
                  <h2
                    className="mb-3 mt-5 text-lg font-bold text-slate-900 first:mt-0"
                    {...props}
                  />
                ),

                h3: ({ node, ...props }) => (
                  <h3
                    className="mb-2 mt-4 text-base font-semibold text-slate-900"
                    {...props}
                  />
                ),

                p: ({ node, ...props }) => (
                  <p
                    className="mb-3 leading-7 last:mb-0"
                    {...props}
                  />
                ),

                strong: ({ node, ...props }) => (
                  <strong
                    className="font-semibold text-slate-900"
                    {...props}
                  />
                ),

                ul: ({ node, ...props }) => (
                  <ul
                    className="mb-4 list-disc space-y-2 pl-5"
                    {...props}
                  />
                ),

                ol: ({ node, ...props }) => (
                  <ol
                    className="mb-4 list-decimal space-y-2 pl-5"
                    {...props}
                  />
                ),

                li: ({ node, ...props }) => (
                  <li
                    className="pl-1 leading-7"
                    {...props}
                  />
                ),

                table: ({ node, ...props }) => (
                  <div className="my-4 w-full overflow-x-auto rounded-xl border border-slate-200">
                    <table
                      className="w-full min-w-[650px] border-collapse text-left text-xs"
                      {...props}
                    />
                  </div>
                ),

                thead: ({ node, ...props }) => (
                  <thead
                    className="bg-slate-50"
                    {...props}
                  />
                ),

                th: ({ node, ...props }) => (
                  <th
                    className="whitespace-nowrap border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-600"
                    {...props}
                  />
                ),

                tbody: ({ node, ...props }) => (
                  <tbody
                    className="divide-y divide-slate-100"
                    {...props}
                  />
                ),

                tr: ({ node, ...props }) => (
                  <tr
                    className="transition-colors hover:bg-slate-50"
                    {...props}
                  />
                ),

                td: ({ node, ...props }) => (
                  <td
                    className="px-4 py-3 align-top text-slate-700"
                    {...props}
                  />
                ),

                code: ({ node, ...props }) => (
                  <code
                    className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-800"
                    {...props}
                  />
                ),

                blockquote: ({ node, ...props }) => (
                  <blockquote
                    className="my-4 border-l-2 border-emerald-500 bg-slate-50 px-4 py-3 text-slate-600"
                    {...props}
                  />
                ),

                hr: ({ node, ...props }) => (
                  <hr
                    className="my-5 border-slate-200"
                    {...props}
                  />
                ),
              }}
            >
              {message.content}
            </ReactMarkdown>
          </div>
        )}
      </div>
    </div>
  );
}

export default function ClientAI() {
  const {
    user,
    activeWorkspace,
    workspaceError,
  } = useClient();

  const [query, setQuery] = useState("");
  const [messages, setMessages] = useState([]);
  const [isThinking, setIsThinking] = useState(false);
  const [error, setError] = useState("");

  const scrollRef = useRef(null);
  const textareaRef = useRef(null);

  const requestVersionRef = useRef(0);
  const pendingRef = useRef(false);
  const mountedRef = useRef(false);

  const organizationId =
    activeWorkspace?.workspaceType === "client"
      ? activeWorkspace.id
      : null;

  const organizationName =
    activeWorkspace?.name || "Your Business";

  const scopeKey =
    user?.id && organizationId
      ? `${user.id}:${organizationId}`
      : null;

  const currentScopeRef = useRef(scopeKey);

  // Updated during render so in-flight responses
  // cannot be applied after a workspace change.
  currentScopeRef.current = scopeKey;

  const canUseAI = Boolean(
    scopeKey && !workspaceError
  );

  const hasMessages = messages.length > 0;

  /* ========================================================
     MOUNT / UNMOUNT
  ======================================================== */

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      requestVersionRef.current += 1;
      pendingRef.current = false;
    };
  }, []);

  /* ========================================================
     ORGANIZATION ISOLATION

     Never reuse previous chat messages when
     the user switches to another organization.
  ======================================================== */

  useEffect(() => {
    requestVersionRef.current += 1;
    pendingRef.current = false;

    setMessages([]);
    setQuery("");
    setError("");
    setIsThinking(false);

    return () => {
      requestVersionRef.current += 1;
    };
  }, [scopeKey]);

  /* ========================================================
     AUTO SCROLL
  ======================================================== */

  useEffect(() => {
    const container = scrollRef.current;

    if (!container) return;

    container.scrollTop =
      container.scrollHeight;
  }, [messages, isThinking]);

  /* ========================================================
     TEXTAREA SIZE
  ======================================================== */

  useEffect(() => {
    const textarea = textareaRef.current;

    if (!textarea) return;

    textarea.style.height = "auto";

    textarea.style.height = `${Math.min(
      textarea.scrollHeight,
      120
    )}px`;
  }, [query]);

  /* ========================================================
     SEND MESSAGE
  ======================================================== */

  const handleSend = async (override = null) => {
    const text = String(
      override ?? query
    ).trim();

    if (
      !text ||
      !canUseAI ||
      pendingRef.current
    ) {
      return;
    }

    if (text.length > MAX_MESSAGE_LENGTH) {
      setError(
        `Questions must be ${MAX_MESSAGE_LENGTH} characters or fewer.`
      );
      return;
    }

    const requestScope = scopeKey;
    const requestId =
      ++requestVersionRef.current;

    const selectedWorkspace = {
      ...activeWorkspace,
    };

    const previousHistory = messages
      .slice(-MAX_HISTORY)
      .map(({ role, content }) => ({
        role,
        content,
      }));

    pendingRef.current = true;

    setQuery("");
    setError("");
    setIsThinking(true);

    setMessages((previous) => [
      ...previous,
      createMessage("user", text),
    ]);

    try {
      const response =
        await aiService.askClientAssistant(
          text,
          previousHistory,
          selectedWorkspace
        );

      if (
        !mountedRef.current ||
        requestVersionRef.current !== requestId ||
        currentScopeRef.current !== requestScope
      ) {
        return;
      }

      if (
        typeof response?.message !== "string" ||
        !response.message.trim()
      ) {
        throw new Error(
          "ProfitYo AI returned an empty response."
        );
      }

      setMessages((previous) => [
        ...previous,
        createMessage(
          "assistant",
          response.message
        ),
      ]);
    } catch (requestError) {
      if (
        !mountedRef.current ||
        requestVersionRef.current !== requestId ||
        currentScopeRef.current !== requestScope
      ) {
        return;
      }

      setError(
        requestError?.message ||
          "Unable to process your question. Please try again."
      );
    } finally {
      if (
        mountedRef.current &&
        requestVersionRef.current === requestId &&
        currentScopeRef.current === requestScope
      ) {
        pendingRef.current = false;
        setIsThinking(false);
      }
    }
  };

  /* ========================================================
     NEW CHAT
  ======================================================== */

  const handleNewChat = () => {
    requestVersionRef.current += 1;
    pendingRef.current = false;

    setMessages([]);
    setQuery("");
    setError("");
    setIsThinking(false);

    textareaRef.current?.focus();
  };

  /* ========================================================
     KEYBOARD
  ======================================================== */

  const handleKeyDown = (event) => {
    if (
      event.key === "Enter" &&
      !event.shiftKey &&
      !event.nativeEvent.isComposing
    ) {
      event.preventDefault();
      void handleSend();
    }
  };

  /* ========================================================
     RENDER
  ======================================================== */

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden bg-white">

      {/* AI HEADER */}

      <header className="flex shrink-0 items-center justify-between gap-3 border-b border-slate-200 px-4 py-4 sm:px-6">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-white">
            <FiCpu className="text-xl" />
          </div>

          <div className="min-w-0">
            <h2 className="text-sm font-bold text-slate-900">
              ProfitYo AI Copilot
            </h2>

            <p className="truncate text-xs text-slate-500">
              {organizationName}
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={handleNewChat}
          disabled={
            !hasMessages &&
            !query &&
            !error
          }
          className="flex shrink-0 items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-40"
        >
          <FiPlus />
          New Chat
        </button>
      </header>

      {/* CHAT AREA */}

      <main
        ref={scrollRef}
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-6 sm:px-8"
      >
        <div className="mx-auto flex min-h-full w-full max-w-3xl flex-col">

          {!hasMessages ? (
            <div className="my-auto py-8">

              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700">
                <FiCpu className="text-2xl" />
              </div>

              <h2 className="mt-6 text-center text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                How can I help your business?
              </h2>

              <p className="mx-auto mt-3 max-w-xl text-center text-sm leading-7 text-slate-500">
                Ask questions about the available
                financial records for{" "}
                <span className="font-semibold text-slate-800">
                  {organizationName}
                </span>
                .
              </p>

              {/* SUGGESTED QUESTIONS */}

              <div className="mt-9 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {SUGGESTIONS.map((item) => {
                  const Icon = item.icon;

                  return (
                    <button
                      key={item.title}
                      type="button"
                      disabled={
                        !canUseAI ||
                        isThinking
                      }
                      onClick={() =>
                        void handleSend(item.prompt)
                      }
                      className="group flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-left transition-colors hover:border-emerald-300 hover:bg-emerald-50/40 disabled:opacity-50"
                    >
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
                        <Icon className="text-lg" />
                      </div>

                      <div>
                        <p className="text-sm font-semibold text-slate-900">
                          {item.title}
                        </p>

                        <p className="mt-1 text-xs leading-5 text-slate-500">
                          {item.description}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="mt-8 flex items-center justify-center gap-2 text-xs text-slate-400">
                <FiShield />
                Questions are scoped to your
                selected organization
              </div>
            </div>
          ) : (
            <div
              aria-live="polite"
              className="flex flex-col gap-5"
            >
              {messages.map((message) => (
                <ChatBubble
                  key={message.id}
                  message={message}
                />
              ))}

              {isThinking && (
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-900 text-white">
                    <FiCpu />
                  </div>

                  <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500">
                    <FiLoader className="animate-spin" />
                    Analyzing available records...
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* CHAT COMPOSER */}

      <footer className="shrink-0 border-t border-slate-200 bg-white px-4 pb-4 pt-3 sm:px-8">
        <div className="mx-auto w-full max-w-3xl">

          {error && (
            <div
              role="alert"
              className="mb-3 flex items-start gap-2 rounded-xl border border-rose-100 bg-rose-50 p-3 text-xs text-rose-700"
            >
              <FiAlertCircle className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {workspaceError && (
            <div
              role="alert"
              className="mb-3 rounded-xl bg-rose-50 p-3 text-xs text-rose-700"
            >
              {workspaceError}
            </div>
          )}

          <div className="flex items-end gap-2 rounded-2xl border border-slate-200 bg-white p-2 shadow-sm focus-within:border-emerald-300">

            <textarea
              ref={textareaRef}
              rows={1}
              maxLength={MAX_MESSAGE_LENGTH}
              value={query}
              disabled={!canUseAI}
              onChange={(event) =>
                setQuery(event.target.value)
              }
              onKeyDown={handleKeyDown}
              placeholder={
                canUseAI
                  ? "Ask about your business finances..."
                  : "Select an authorized client workspace..."
              }
              aria-label="Ask ProfitYo AI Copilot"
              className="max-h-[120px] min-h-[42px] min-w-0 flex-1 resize-none overflow-y-auto bg-transparent px-2 py-2.5 text-sm leading-5 text-slate-900 outline-none placeholder:text-slate-400 disabled:opacity-50"
            />

            <button
              type="button"
              onClick={() =>
                void handleSend()
              }
              disabled={
                !canUseAI ||
                !query.trim() ||
                isThinking
              }
              aria-label="Send message"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
            >
              {isThinking ? (
                <FiLoader className="animate-spin" />
              ) : (
                <FiArrowUp />
              )}
            </button>
          </div>

          <p className="mt-2 text-center text-[11px] text-slate-400">
            Enter to send · Shift + Enter for a new line
          </p>

          <p className="mt-1 text-center text-[11px] leading-5 text-slate-400">
            AI responses may contain errors.
            Verify important accounting information.
          </p>
        </div>
      </footer>
    </div>
  );
}